import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlossaryFormItem } from "Types/Admin/GlossaryFormItem";

type Props = {
  item: GlossaryFormItem | null;
  onCancel: () => void;
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e6e6e6",
  padding: 16,
  borderRadius: 6,
};

const fieldStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  borderRadius: 4,
  border: "1px solid #d9d9d9",
  backgroundColor: "#f5f5f5",
  color: "#666",
};

const GlossaryView: React.FC<Props> = ({ item, onCancel }) => {
  const [date, setDate] = useState("");
  const [title, setTitle] = useState("");
  const [glossaryDescription, setGlossaryDescription] = useState("");
  const [glossaryDescription2, setGlossaryDescription2] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [pin, setPin] = useState("");
  const [sections, setSections] = useState<
    Array<{ title: string; description: string }>
  >([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!item) return;

    setTitle(item.title ?? "");
    setDate(item.date ?? "");
    setGlossaryDescription(item.glossaryDescription ?? "");
    setGlossaryDescription2(item.glossaryDescription2 ?? "");
    setAddress(item.address ?? "");
    setCity(item.city ?? "");
    setState(item.state ?? "");
    setCountry(item.country ?? "");
    setPin(item.pin ?? "");

    const mappedSections = Array.from({ length: 8 }, (_, i) => ({
      title: String(
        item[`section${i + 1}Title` as keyof GlossaryFormItem] ?? "",
      ),
      description: String(
        item[`section${i + 1}Description` as keyof GlossaryFormItem] ?? "",
      ),
    }));

    setSections(mappedSections);
  }, [item]);

  if (!item) return <div>Loading...</div>;

  return (
    <>
      <button onClick={onCancel} className="text-blue-600 mb-3">
        ← Back
      </button>

      <h3 className="mb-4 text-lg font-semibold">View Gallery</h3>

      <div style={cardStyle}>
        {/* ===== BASIC INFO GRID ===== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">Date</label>
            <input value={date} style={fieldStyle} disabled />
          </div>

          <div>
            <label className="block mb-1 font-medium">Title</label>
            <input value={title} style={fieldStyle} disabled />
          </div>
        </div>

        {/* Glossary Descriptions */}
        {(glossaryDescription || glossaryDescription2) && (
          <div className="mt-4 space-y-4">
            {glossaryDescription && (
              <div>
                <label className="block mb-1 font-medium">
                  Gallery Description
                </label>
                <textarea
                  value={glossaryDescription}
                  style={{ ...fieldStyle, minHeight: 80 }}
                  disabled
                />
              </div>
            )}

            {glossaryDescription2 && (
              <div>
                <label className="block mb-1 font-medium">
                  Gallery Description 2
                </label>
                <textarea
                  value={glossaryDescription2}
                  style={{ ...fieldStyle, minHeight: 100 }}
                  disabled
                />
              </div>
            )}
          </div>
        )}

        {/* Address Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {address && (
            <div>
              <label className="block mb-1 font-medium">Address</label>
              <input value={address} style={fieldStyle} disabled />
            </div>
          )}

          {city && (
            <div>
              <label className="block mb-1 font-medium">City</label>
              <input value={city} style={fieldStyle} disabled />
            </div>
          )}

          {state && (
            <div>
              <label className="block mb-1 font-medium">State</label>
              <input value={state} style={fieldStyle} disabled />
            </div>
          )}

          {country && (
            <div>
              <label className="block mb-1 font-medium">Country</label>
              <input value={country} style={fieldStyle} disabled />
            </div>
          )}

          {pin && (
            <div>
              <label className="block mb-1 font-medium">PIN Code</label>
              <input value={pin} style={fieldStyle} disabled />
            </div>
          )}
        </div>

        {/* ===== SECTIONS ===== */}
        {sections.map(
          (s, i) =>
            (s.title || s.description) && (
              <div key={i} className="mt-6 border-t pt-4">
                <label className="block mb-1 font-medium">
                  Section {i + 1} Title
                </label>
                <input value={s.title} style={fieldStyle} disabled />

                <label className="mt-3 block mb-1 font-medium">
                  Section {i + 1} Description
                </label>
                <textarea
                  value={s.description}
                  style={{ ...fieldStyle, minHeight: 100 }}
                  disabled
                />
              </div>
            ),
        )}

        {/* ===== IMAGES ===== */}
        {item.imagesPreviewUrls?.length ? (
          <div className="mt-6 border-t pt-4">
            <label className="block mb-2 font-medium">
              Images ({item.imagesPreviewUrls.length})
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {item.imagesPreviewUrls.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`${idx + 1}`}
                  className="h-32 w-full object-cover rounded border"
                />
              ))}
            </div>
          </div>
        ) : null}

        {/* FOOTER */}
        <div className="mt-8 flex justify-end gap-3 border-t pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            Back to List
          </button>
          <button
            type="button"
            onClick={() =>
              navigate(`/admin/other-details/glossary/edit/${item.id}`, {
                state: { item, mode: "edit" },
              })
            }
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Edit Gallery
          </button>
        </div>
      </div>
    </>
  );
};

export default GlossaryView;
